import { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";

const SUBSCRIPTION_OPTIONS = [
  { label: "1 month", value: 1 },
  { label: "3 months", value: 3 },
  { label: "6 months", value: 6 },
  { label: "12 months", value: 12 },
  { label: "24 months", value: 24 },
];

function ToggleField({ label, description, checked, onChange }) {
  return (
    <label className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-rounded-[20px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
      <div>
        <p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">
          {label}
        </p>
        <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`tw-relative tw-h-7 tw-w-12 tw-rounded-full tw-transition ${
          checked ? "tw-bg-[#F35658]" : "tw-bg-[rgba(44,50,85,0.12)]"
        }`}
      >
        <span
          className={`tw-absolute tw-top-1 tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow-sm tw-transition ${
            checked ? "tw-left-6" : "tw-left-1"
          }`}
        />
      </button>
    </label>
  );
}

function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  min,
  required = false,
}) {
  return (
    <label className="tw-block">
      <span className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
        {label}
      </span>
      <input
        type={type}
        value={value}
        min={min}
        required={required}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] focus:tw-border-[rgba(243,86,88,0.5)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.08)]"
      />
    </label>
  );
}

export default function EnterpriseAccountFormSheet({
  open,
  account,
  loading,
  error,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState({
    organizationName: "",
    firstName: "",
    lastName: "",
    email: "",
    subscriptionPeriod: 12,
    cards: 25,
    webhookAccess: false,
    emailAuth: true,
  });

  useEffect(() => {
    if (!open) return;

    setForm({
      organizationName: account?.organizationName || "",
      firstName: account?.firstName || "",
      lastName: account?.lastName || "",
      email: account?.email || "",
      subscriptionPeriod: Number(account?.subscriptionPeriod || 12),
      cards: Number(account?.cards || account?.totalCards || 25),
      webhookAccess: Boolean(account?.webhookAccess),
      emailAuth: account?.emailAuth !== false,
    });
  }, [account, open]);

  const title = useMemo(
    () => (account?.id ? "Edit Enterprise Account" : "Create Enterprise Account"),
    [account?.id]
  );

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...form,
      subscriptionPeriod: Number(form.subscriptionPeriod),
      cards: Number(form.cards),
    });
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[90] tw-bg-[rgba(15,23,42,0.42)] tw-backdrop-blur-[2px]">
      <div className="tw-flex tw-h-full tw-justify-end">
        <button
          type="button"
          className="tw-h-full tw-flex-1"
          aria-label="Close panel"
          onClick={onClose}
        />
        <aside className="tw-h-full tw-w-full tw-max-w-[580px] tw-overflow-y-auto tw-bg-[#fbfcfe] tw-shadow-[-24px_0_60px_rgba(20,26,44,0.18)]">
          <div className="tw-sticky tw-top-0 tw-z-10 tw-border-b tw-border-[rgba(31,41,64,0.08)] tw-bg-[rgba(251,252,254,0.94)] tw-backdrop-blur-md">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-p-5 lg:tw-p-6">
              <div className="tw-space-y-2">
                <p className="admin-v2-eyebrow">Enterprise Editor</p>
                <h2 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[var(--admin-v2-ink)]">
                  {title}
                </h2>
                {/* <p className="admin-v2-text-muted">
                  Bring the legacy enterprise workflow into Admin 2.0 with cleaner controls, security flags, and package limits.
                </p> */}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="tw-space-y-6 tw-p-5 lg:tw-p-6">
            <section className="admin-v2-soft-panel tw-p-5">
              <div className="tw-grid tw-gap-4">
                <InputField
                  label="Workspace name"
                  value={form.organizationName}
                  onChange={(value) => setForm((current) => ({ ...current, organizationName: value }))}
                  placeholder="Cardly London"
                />

                <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                  <InputField
                    label="First name"
                    value={form.firstName}
                    onChange={(value) => setForm((current) => ({ ...current, firstName: value }))}
                    placeholder="Sarah"
                    required
                  />
                  <InputField
                    label="Last name"
                    value={form.lastName}
                    onChange={(value) => setForm((current) => ({ ...current, lastName: value }))}
                    placeholder="Connor"
                    required
                  />
                </div>

                <InputField
                  label="Admin email"
                  type="email"
                  value={form.email}
                  onChange={(value) => setForm((current) => ({ ...current, email: value }))}
                  placeholder="owner@company.com"
                  required
                />
              </div>
            </section>

            <section className="admin-v2-soft-panel tw-p-5">
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <label className="tw-block">
                  <span className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                    Subscription period
                  </span>
                  <select
                    value={form.subscriptionPeriod}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        subscriptionPeriod: Number(event.target.value),
                      }))
                    }
                    className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] focus:tw-border-[rgba(243,86,88,0.5)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.08)]"
                  >
                    {SUBSCRIPTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <InputField
                  label="Cardholder limit"
                  type="number"
                  min={1}
                  value={form.cards}
                  onChange={(value) => setForm((current) => ({ ...current, cards: value }))}
                  placeholder="25"
                  required
                />
              </div>
            </section>

            <section className="tw-grid tw-gap-4">
              <ToggleField
                label="Webhook access"
                description="Allow this enterprise workspace to use webhook-connected integrations."
                checked={form.webhookAccess}
                onChange={(value) => setForm((current) => ({ ...current, webhookAccess: value }))}
              />
              <ToggleField
                label="Email authentication"
                description="Keep password and email-based sign-in available for the assigned admin."
                checked={form.emailAuth}
                onChange={(value) => setForm((current) => ({ ...current, emailAuth: value }))}
              />
            </section>

            {error ? (
              <div className="admin-v2-status-note tw-p-4 tw-text-sm">{error}</div>
            ) : null}

            <div className="tw-flex tw-flex-col-reverse tw-gap-3 sm:tw-flex-row sm:tw-justify-end">
              <button
                type="button"
                onClick={onClose}
                className="admin-v2-action admin-v2-action-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="admin-v2-action admin-v2-action-primary disabled:tw-cursor-not-allowed disabled:tw-opacity-70"
              >
                {loading ? <Loader2 size={16} className="tw-animate-spin" /> : null}
                {account?.id ? "Update enterprise" : "Create Enterprise"}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </div>
  );
}
